/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any
import { EntityForm } from '@/components/forms/EntityForm';
import { LayoutToFormValues } from '@/components/forms/fields/types';
import { ticketBulkEditLayout } from '@/features/tickets/ticket.schemas';
import { useCurrentUser } from '@/features/users/users.hooks';
import { useEntityForm } from '@/hooks/useEntityForm';
import { sanitizeFormData } from '@/lib/form-utils';
import {
  assertNever,
  createTicketEvent,
  Ticket,
  UpdateTicketEvent,
  UpdateTicketPayload,
} from '@certhon/models';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { usePostTicketEventsMutation } from '../tickets.hooks';

interface BulkTicketEditFormProps {
  selectedTickets: Ticket[];
  onClose: () => void;
}

// Default form values
const defaultValues: LayoutToFormValues<typeof ticketBulkEditLayout> = {
  add_labels: [],
  remove_labels: [],
  status: undefined,
  due: undefined,
  assignee_user_id: undefined,
  executor_user_id: undefined,
  project_id: undefined,
  werk_id: undefined,
};

export function BulkTicketEditForm({
  selectedTickets,
  onClose,
}: BulkTicketEditFormProps) {
  const user = useCurrentUser();

  // TODO: Get unique labels from selected tickets for the remove labels field
  // const availableLabelsForRemoval = useMemo(() => {
  //   const labelSet = new Set<number>();
  //   selectedTickets.forEach(ticket => {
  //     ticket.labels.forEach(labelId => labelSet.add(labelId));
  //   });
  //   return Array.from(labelSet);
  // }, [selectedTickets]);

  const bulkForm = useEntityForm({
    layout: ticketBulkEditLayout,
    data: defaultValues as any,
    isLoading: false, // should no be neseccary
    // defaultValues: defaultValues as any,
  });

  const updateTicketsMutation = usePostTicketEventsMutation();

  const handleSubmit = bulkForm.form.handleSubmit(async (formData) => {
    const bulkOperationId = uuidv4();
    const events: UpdateTicketEvent[] = [];
    // The FORM may hold a null chain; a PAYLOAD may not. werk_id null is
    // the project-level shape; the other two mean unresolved.
    const { project_id, werk_id, client_id, ...rest } =
      sanitizeFormData(formData);
    const payload: UpdateTicketPayload = { ...rest };

    const touchesChain =
      project_id !== undefined ||
      werk_id !== undefined ||
      client_id !== undefined;
    if (touchesChain) {
      if (project_id == null || client_id == null) {
        toast.error('Bulk edit failed', {
          description:
            'Could not resolve the project and customer for the selected reference',
        });
        return;
      }
      payload.project_id = project_id;
      payload.werk_id = werk_id ?? null;
      payload.client_id = client_id;
    }

    // Only create update-events for tickets that would actually be affected by this operation
    // Also customize the payloads so only actual changes ar made (e.g. dont close closed tickets, dont add an allready present label, etc)
    selectedTickets.forEach((ticket) => {
      const event = createTicketEvent('UPDATE_TICKET', {
        ticket_id: ticket.id,
        user_id: user!.recnum,
        payload: { ...payload }, // copy, we are going to delete keys
        meta: { bulkOperationId },
      });

      Object.keys(event.payload).forEach((_k) => {
        const key = _k as keyof UpdateTicketPayload;

        switch (key) {
          case 'add_labels':
            event.payload.add_labels = event.payload.add_labels!.filter(
              (l) => !ticket.labels.includes(l),
            );
            if (!event.payload.add_labels.length) {
              delete event.payload.add_labels;
            }
            break;
          case 'remove_labels':
            event.payload.remove_labels = event.payload.remove_labels!.filter(
              (l) => ticket.labels.includes(l),
            );
            if (!event.payload.remove_labels.length) {
              delete event.payload.remove_labels;
            }
            break;
          case 'title':
          case 'description':
          case 'status':
          case 'due':
          case 'project_id':
          case 'werk_id':
          case 'client_id':
          case 'assignee_user_id':
          case 'executor_user_id':
            if (ticket[key] === event.payload[key]) {
              delete event.payload[key];
            }
            break;
          default:
            assertNever(key);
        }
      });

      // CRM-chain keys are all-or-nothing: the per-key no-op cleanup above
      // may strip only part of the chain (e.g. the ticket already sits under
      // the target project) — a partial chain is rejected by the schema, so
      // restore the full chain when any of it survived.
      const chainKeys = ['project_id', 'werk_id', 'client_id'] as const;
      const survived = chainKeys.filter((k) => k in event.payload);
      if (survived.length > 0 && survived.length < chainKeys.length) {
        // Narrowed above, so these two are numbers here.
        event.payload.project_id = payload.project_id;
        event.payload.werk_id = payload.werk_id ?? null;
        event.payload.client_id = payload.client_id;
      }

      if (Object.keys(event.payload).length) {
        events.push(event);
      }
    });

    // TODO: describe how ticket bulk edit operates in the docs and reference in toasts and form validation
    if (events.length > 0) {
      await updateTicketsMutation.mutateAsync(events);
      onClose();
    } else {
      // TODO: BulkTicketEdit. impove UX by handling 'no effect' case during form validation a/o form options
      toast.warning('Bulk Edit would have no effect.');
    }
  });

  return (
    <EntityForm
      {...bulkForm}
      formMode="edit"
      onSubmit={handleSubmit}
      onCancel={onClose}
    />
  );
}
